import React, { useEffect, useState } from 'react';
import secureStorage from '../utils/secureStorage';
import axios from 'axios';
import { useAgencyContext } from '../contexts/AppContexts';
import { ArrowRight, CheckSquare, Edit, LogIn, LogOut, Mail, MapPin, Phone, Plus, Printer, RefreshCw, Search, Square, Target, Trash2, Users, X } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { API_BASE_URL } from '../config';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const ParticipantsList = ({ onAddNew, onEdit, positions = [] }) => {
  const { selectedAgency } = useAgencyContext();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' or 'participating'
  const [lastSynced, setLastSynced] = useState(null);
  const [syncingToUsers, setSyncingToUsers] = useState(false);

  // Full bi-directional sync — runs BOTH:
  //   1) Users → Participants (creates missing participants + reconciles matched pairs)
  //   2) Participants → Users (reconciles matched pairs — never creates users, so
  //      unlicensed helpers who exist as participants only stay that way)
  const handleSyncParticipantsToUsers = async () => {
    if (!window.confirm(
      'Full Sync (Users ↔ Participants)?\n\n' +
      "• Every user in this agency without a matching Participant will get one created automatically (name / email / phone / avatar carried over).\n" +
      "• Every matched User/Participant pair (by email) will be reconciled so both sides share the same name, email, phone and profile image.\n" +
      "• Participants without a matching user are LEFT ALONE — no user is ever created from a participant, so unlicensed helpers remain participants only.\n\n" +
      'Continue?'
    )) return;

    setSyncingToUsers(true);
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [u2p, p2u] = await Promise.all([
        axios.post(`${API_BASE_URL}/api/users/sync-to-participants`, {}, { headers }),
        axios.post(`${API_BASE_URL}/api/participants/sync-to-users`, {}, { headers }),
      ]);
      const created = u2p.data?.created ?? 0;
      const reconciledU2P = u2p.data?.reconciled ?? 0;
      const reconciledP2U = p2u.data?.reconciled ?? 0;
      const skippedNoUser = p2u.data?.skipped_no_matching_user ?? 0;
      const totalReconciled = Math.max(reconciledU2P, reconciledP2U);
      alert(
        `Full Sync complete — ${created} new participant${created === 1 ? '' : 's'} created, ` +
        `${totalReconciled} matched pair${totalReconciled === 1 ? '' : 's'} reconciled, ` +
        `${skippedNoUser} participant${skippedNoUser === 1 ? '' : 's'} left as participant-only (no matching user).`
      );
      // Refresh the list
      fetchParticipants();
    } catch (err) {
      console.error('Sync failed:', err);
      const msg = err.response?.data?.detail || err.message || 'Sync failed';
      alert(`Could not sync: ${msg}`);
    } finally {
      setSyncingToUsers(false);
    }
  };
  
  // Bulk selection state - persisted in localStorage
  const [selectedParticipants, setSelectedParticipants] = useState(() => {
    const saved = secureStorage.getPreference('exrsim-selected-participants');
    return saved ? JSON.parse(saved) : [];
  });
  const [exercises, setExercises] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState('');
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('lastName'); // 'lastName' or 'firstName'

  // Persist selection to localStorage
  useEffect(() => {
    secureStorage.setPreference('exrsim-selected-participants', JSON.stringify(selectedParticipants));
  }, [selectedParticipants]);

  useEffect(() => {
    fetchParticipants();
    fetchExercisesForBulk();
  }, [selectedAgency]); // Re-fetch when agency context changes

  // Setup polling for real-time sync (every 30 seconds)
  useEffect(() => {
    const pollInterval = setInterval(() => {
      fetchParticipants(true); // Silent refresh
    }, 30000);
    
    return () => clearInterval(pollInterval);
  }, [selectedAgency]);

  const fetchParticipants = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Add organization_id filter if Super Admin has selected an agency
      let url = `${API}/participants`;
      if (selectedAgency) {
        url += `?organization_id=${selectedAgency.id}`;
      }
      
      const response = await axios.get(url, { headers });
      setParticipants(response.data);
      setLastSynced(new Date());
    } catch (error) {
      if (!silent) {
        console.error('Error fetching participants:', error);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Manual refresh function
  const refreshParticipants = async () => {
    await fetchParticipants(false);
  };

  const fetchExercisesForBulk = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/exercise-builder`, { headers });
      setExercises(response.data || []);
    } catch (error) {
      console.error('Error fetching exercises:', error);
    }
  };

  // Selection handlers
  const handleSelectParticipant = (participantId) => {
    setSelectedParticipants(prev => {
      if (prev.includes(participantId)) {
        return prev.filter(id => id !== participantId);
      } else {
        return [...prev, participantId];
      }
    });
  };

  const handleSelectAll = () => {
    const allIds = filteredParticipants.map(p => p.id);
    setSelectedParticipants(allIds);
  };

  const handleClearSelection = () => {
    setSelectedParticipants([]);
    setSelectedExercise('');
  };

  // Bulk assign handler
  const handleBulkAssign = async () => {
    if (!selectedExercise || selectedParticipants.length === 0) return;
    
    setBulkAssigning(true);
    const isRemovingFromExercise = selectedExercise === 'no-exercise';
    
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Update each selected participant
      const updatePromises = selectedParticipants.map(participantId => {
        const participant = participants.find(p => p.id === participantId);
        if (participant) {
          return axios.put(`${API}/participants/${participantId}`, {
            ...participant,
            assigned_exercise: isRemovingFromExercise ? '' : selectedExercise,
            involvedInExercise: isRemovingFromExercise ? false : true
          }, { headers });
        }
        return Promise.resolve();
      });
      
      await Promise.all(updatePromises);
      
      // Refresh participants list
      await fetchParticipants();
      
      // Clear selection
      handleClearSelection();
      setShowBulkConfirm(false);
      
      if (isRemovingFromExercise) {
        alert(`Successfully removed ${selectedParticipants.length} participant(s) from their exercises.`);
      } else {
        alert(`Successfully assigned ${selectedParticipants.length} participant(s) to the exercise.`);
      }
    } catch (error) {
      console.error('Error bulk assigning participants:', error);
      alert('Error updating participants. Please try again.');
    } finally {
      setBulkAssigning(false);
    }
  };

  // Toggle a single participant's Checked In status via PUT
  const handleToggleCheckedIn = async (participant) => {
    const newVal = !participant.checkedIn;
    // Optimistic UI update
    setParticipants(prev => prev.map(p => p.id === participant.id ? { ...p, checkedIn: newVal } : p));
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/participants/${participant.id}`, { ...participant, checkedIn: newVal }, { headers });
    } catch (err) {
      console.error('Toggle checked-in failed:', err);
      // Revert on error
      setParticipants(prev => prev.map(p => p.id === participant.id ? { ...p, checkedIn: !newVal } : p));
      alert('Could not update Checked In status. Please try again.');
    }
  };

  // Bulk Check In / Check Out for all currently filtered participants
  const [bulkCheckInLoading, setBulkCheckInLoading] = useState(false);
  const handleBulkCheckInOut = async (checkedInValue) => {
    const targets = filteredParticipants.filter(p => (p.checkedIn || false) !== checkedInValue);
    if (targets.length === 0) {
      alert(checkedInValue
        ? 'Everyone visible is already checked in.'
        : 'Everyone visible is already checked out.');
      return;
    }
    const label = checkedInValue ? 'check IN' : 'check OUT';
    if (!window.confirm(`This will ${label} ${targets.length} visible participant${targets.length !== 1 ? 's' : ''}. Continue?`)) return;

    setBulkCheckInLoading(true);
    // Optimistic UI update
    const targetIds = new Set(targets.map(t => t.id));
    setParticipants(prev => prev.map(p => targetIds.has(p.id) ? { ...p, checkedIn: checkedInValue } : p));
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await Promise.all(targets.map(p =>
        axios.put(`${API}/participants/${p.id}`, { ...p, checkedIn: checkedInValue }, { headers })
      ));
    } catch (err) {
      console.error('Bulk check in/out failed:', err);
      alert('Some updates failed. Refreshing list.');
      fetchParticipants();
    } finally {
      setBulkCheckInLoading(false);
    }
  };

  const getSelectedExerciseName = () => {
    if (selectedExercise === 'no-exercise') {
      return 'No Exercise Assigned';
    }
    const exercise = exercises.find(e => e.id === selectedExercise);
    return exercise ? exercise.exercise_name : 'Unknown Exercise';
  };

  const filteredParticipants = participants.filter(participant => {
    // First apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = 
        (participant.name && participant.name.toLowerCase().includes(search)) ||
        (participant.firstName && participant.firstName.toLowerCase().includes(search)) ||
        (participant.lastName && participant.lastName.toLowerCase().includes(search)) ||
        (participant.email && participant.email.toLowerCase().includes(search)) ||
        (participant.position && participant.position.toLowerCase().includes(search)) ||
        (participant.organization && participant.organization.toLowerCase().includes(search)) ||
        (participant.cellPhone && participant.cellPhone.includes(search)) ||
        (participant.phone && participant.phone.includes(search));
      
      if (!matchesSearch) return false;
    }
    
    // Then apply filter dropdown
    if (filter === 'participating') {
      return participant.involvedInExercise === true;
    }
    if (filter === 'not-participating') {
      return participant.involvedInExercise !== true;
    }
    if (filter === 'no-exercise') {
      // Check both camelCase and snake_case versions
      const assignedEx = participant.assigned_exercise || participant.assignedExercise;
      return !assignedEx || assignedEx === '';
    }
    if (filter.startsWith('exercise-')) {
      const exerciseId = filter.replace('exercise-', '');
      // Check both camelCase and snake_case versions
      const assignedEx = participant.assigned_exercise || participant.assignedExercise;
      return assignedEx === exerciseId;
    }
    return true; // 'all' - show all participants regardless of exercise
  }).sort((a, b) => {
    // Extract first/last names — fall back to splitting the combined `name` field
    const splitFallback = (p) => {
      const parts = (p.name || '').trim().split(/\s+/);
      return { first: parts[0] || '', last: parts.slice(-1)[0] || '' };
    };
    const aFirst = (a.firstName || splitFallback(a).first || '').toLowerCase();
    const aLast = (a.lastName || splitFallback(a).last || '').toLowerCase();
    const bFirst = (b.firstName || splitFallback(b).first || '').toLowerCase();
    const bLast = (b.lastName || splitFallback(b).last || '').toLowerCase();

    if (sortBy === 'firstName') {
      return aFirst.localeCompare(bFirst) || aLast.localeCompare(bLast);
    }
    // Default: last name, then first name
    return aLast.localeCompare(bLast) || aFirst.localeCompare(bFirst);
  });

  // Print function for Participants
  const printParticipants = () => {
    const currentDateTime = new Date().toLocaleString();
    const printContent = `
      <html>
        <head>
          <title>Exercise Participants Summary</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { border-bottom: 2px solid #333; margin-bottom: 20px; padding-bottom: 10px; }
            .participants-table { width: 100%; border-collapse: collapse; font-size: 11px; }
            .participants-table th { background-color: #333; color: white; padding: 8px 6px; text-align: left; font-weight: bold; }
            .participants-table td { padding: 6px; border-bottom: 1px solid #ddd; vertical-align: middle; }
            .participants-table tr:nth-child(even) { background-color: #f9f9f9; }
            .participants-table tr:hover { background-color: #f0f0f0; }
            .participant-name { font-weight: bold; }
            .status-badge { display: inline-block; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: bold; }
            .status-participating { background-color: #d4edda; color: #155724; }
            .status-not-participating { background-color: #f8f9fa; color: #6c757d; }
            .footer { 
              position: fixed; 
              bottom: 20px; 
              left: 20px; 
              right: 20px; 
              text-align: center; 
              font-size: 12px; 
              color: #666; 
              border-top: 1px solid #ddd; 
              padding-top: 10px; 
            }
            @media print { 
              @page { 
                margin: 20px 20px 80px 20px; 
                size: A4;
              }
              body { 
                margin: 0; 
                padding: 20px 20px 60px 20px; 
                font-size: 12px;
                line-height: 1.4;
              } 
              .no-print { display: none; }
              .footer { 
                position: fixed; 
                bottom: 20px; 
                left: 20px; 
                right: 20px;
                height: 40px;
                z-index: 999;
              }
              .report-item { 
                page-break-inside: avoid; 
                page-break-after: auto;
                margin-bottom: 20px;
              }
              .section-title { 
                page-break-after: avoid;
              }
              .assessment-grid { 
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Exercise Participants Summary</h1>
            <p>Filter: ${filter === 'all' ? 'All Participants' : 'Active Exercise Participants'}</p>
            <p>Generated on: ${new Date().toLocaleDateString()}</p>
          </div>
          <div class="participants-content">
            <table class="participants-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Position</th>
                  <th>Organization</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
            ${filteredParticipants.length > 0 ? 
              filteredParticipants.map((participant, index) => {
              // Display "Last, First" — fall back to splitting the name if needed
              const _nameParts = (participant.name || '').trim().split(/\s+/);
              const _lastN = participant.lastName || _nameParts.slice(-1)[0] || '';
              const _firstN = participant.firstName || _nameParts.slice(0, -1).join(' ') || '';
              const _displayName = (_lastN && _firstN) ? `${_lastN}, ${_firstN}` : (participant.name || _lastN || _firstN || 'Unnamed');
              return `
                  <tr class="participant-row">
                    <td class="participant-name">${_displayName}</td>
                    <td>${participant.position || 'N/A'}</td>
                    <td>${participant.organization || 'N/A'}</td>
                    <td>${participant.email || 'N/A'}</td>
                    <td>${participant.cellPhone || participant.phone || 'N/A'}</td>
                    <td>
                      <span class="status-badge ${participant.involvedInExercise ? 'status-participating' : 'status-not-participating'}">
                        ${participant.involvedInExercise ? 'Active' : 'Not Active'}
                      </span>
                    </td>
                  </tr>
                `;
              }).join('') 
              : '<tr><td colspan="6">No participants match the current filter criteria.</td></tr>'
            }
              </tbody>
            </table>
          </div>
          <div class="footer">
            <p>Generated on: ${currentDateTime} | Powered by EXRSIM</p>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const deleteParticipant = async (participantId) => {
    if (window.confirm('Are you sure you want to delete this participant?')) {
      try {
        const token = secureStorage.getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.delete(`${API}/participants/${participantId}`, { headers });
        setParticipants(prev => prev.filter(p => p.id !== participantId));
      } catch (error) {
        console.error('Error deleting participant:', error);
        alert('Error deleting participant. Please try again.');
      }
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase();
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-orange-500 mb-2">Exercise Participants</h1>
          <p className="text-gray-400">Manage emergency exercise team members</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Label htmlFor="participant-filter" className="text-gray-300 text-sm">
              Filter:
            </Label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger 
                id="participant-filter"
                className="w-56 bg-gray-700 border-gray-600 text-white"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600 max-h-80">
                <SelectItem value="all" className="text-white focus:bg-gray-600">
                  All Participants ({participants.length})
                </SelectItem>
                <SelectItem value="participating" className="text-white focus:bg-gray-600">
                  Involved in Exercise ({participants.filter(p => p.involvedInExercise).length})
                </SelectItem>
                <SelectItem value="not-participating" className="text-white focus:bg-gray-600">
                  Not Involved ({participants.filter(p => !p.involvedInExercise).length})
                </SelectItem>
                <SelectItem value="no-exercise" className="text-white focus:bg-gray-600">
                  No Exercise Assigned ({participants.filter(p => {
                    const assignedEx = p.assigned_exercise || p.assignedExercise;
                    return !assignedEx || assignedEx === '';
                  }).length})
                </SelectItem>
                {exercises.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-xs text-gray-400 border-t border-gray-600 mt-1">
                      Filter by Exercise
                    </div>
                    {exercises.map(exercise => (
                      <SelectItem 
                        key={exercise.id} 
                        value={`exercise-${exercise.id}`} 
                        className="text-white focus:bg-gray-600"
                      >
                        {exercise.exercise_name} ({participants.filter(p => {
                          const assignedEx = p.assigned_exercise || p.assignedExercise;
                          return assignedEx === exercise.id;
                        }).length})
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            className="border-gray-500 text-gray-400 hover:bg-gray-700"
            onClick={refreshParticipants}
            disabled={loading}
            title={lastSynced ? `Last synced: ${lastSynced.toLocaleTimeString()}` : 'Refresh participants'}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
            onClick={printParticipants}
          >
            <Printer className="h-4 w-4 mr-2" />
            Print Participants
          </Button>
          <Button 
            onClick={handleSyncParticipantsToUsers}
            disabled={syncingToUsers}
            variant="outline"
            className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
            data-testid="sync-participants-to-users-btn"
            title="Reconcile Participants with any existing matching Users (never creates new users)"
          >
            <ArrowRight className="h-4 w-4 mr-2" />
            {syncingToUsers ? 'Syncing…' : 'Full Sync (Users ↔ Participants)'}
          </Button>
          <Button 
            onClick={onAddNew}
            className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
            data-testid="add-participant-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Participant
          </Button>
        </div>
      </div>

      {/* Filter Status Indicator */}
      {!loading && participants.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <span>
                Showing {filteredParticipants.length} of {participants.length} participants
              </span>
              {filter === 'participating' && (
                <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                  Involved in Exercise
                </Badge>
              )}
              {filter === 'not-participating' && (
                <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30">
                  Not Involved
                </Badge>
              )}
              {filter === 'no-exercise' && (
                <Badge className="bg-gray-500/20 text-gray-300 border-gray-500/30">
                  No Exercise Assigned
                </Badge>
              )}
              {filter.startsWith('exercise-') && (
              <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                {exercises.find(e => e.id === filter.replace('exercise-', ''))?.exercise_name || 'Exercise'}
              </Badge>
            )}
            </div>
            {lastSynced && (
              <span className="text-xs text-gray-500">
                Auto-syncs every 30s • Last sync: {lastSynced.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Bulk Selection Controls */}
      {!loading && participants.length > 0 && (
        <div className="mb-4 p-4 bg-gray-800 border border-gray-700 rounded-lg">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center space-x-4">
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search participants..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64 bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                disabled={filteredParticipants.length === 0}
                className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
              >
                <CheckSquare className="h-4 w-4 mr-2" />
                Select All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearSelection}
                className="border-gray-500/50 text-gray-400 hover:bg-gray-500/10"
              >
                <Square className="h-4 w-4 mr-2" />
                Clear All
              </Button>
              {/* All Check In / All Check Out (applies to visible/filtered list) */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkCheckInOut(true)}
                disabled={filteredParticipants.length === 0 || bulkCheckInLoading}
                className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                data-testid="participants-all-check-in-btn"
              >
                <LogIn className="h-4 w-4 mr-2" />
                All Check In
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkCheckInOut(false)}
                disabled={filteredParticipants.length === 0 || bulkCheckInLoading}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                data-testid="participants-all-check-out-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                All Check Out
              </Button>
              {/* Sort by (moved from top toolbar) */}
              <div className="flex items-center space-x-2 ml-2">
                <Label htmlFor="participant-sort" className="text-gray-300 whitespace-nowrap text-sm">
                  Sort by:
                </Label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger
                    id="participant-sort"
                    className="w-40 bg-gray-700 border-gray-600 text-white h-9"
                    data-testid="participant-sort-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    <SelectItem value="lastName" className="text-white focus:bg-gray-600">Last Name</SelectItem>
                    <SelectItem value="firstName" className="text-white focus:bg-gray-600">First Name</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {selectedParticipants.length > 0 && (
                <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/30 px-3 py-1">
                  {selectedParticipants.length} participant{selectedParticipants.length !== 1 ? 's' : ''} selected
                </Badge>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Action Bar - Shows when participants are selected */}
      {selectedParticipants.length > 0 && (
        <div className="mb-4 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-orange-300 font-medium">
                {selectedParticipants.length} selected
              </span>
              <div className="flex items-center space-x-2">
                <Label className="text-gray-300 text-sm">Assign to Exercise:</Label>
                <Select value={selectedExercise} onValueChange={setSelectedExercise}>
                  <SelectTrigger className="w-64 bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="Select an exercise..." />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    <SelectItem 
                      value="no-exercise" 
                      className="text-red-400 focus:bg-gray-600"
                    >
                      No Exercise Assigned
                    </SelectItem>
                    <div className="border-t border-gray-600 my-1"></div>
                    {exercises.map((exercise) => (
                      <SelectItem 
                        key={exercise.id} 
                        value={exercise.id} 
                        className="text-white focus:bg-gray-600"
                      >
                        {exercise.exercise_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={() => setShowBulkConfirm(true)}
                disabled={!selectedExercise || bulkAssigning}
                className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
              >
                {bulkAssigning ? 'Assigning...' : 'Assign to Exercise'}
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearSelection}
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
            >
              <X className="h-4 w-4 mr-2" />
              Clear Selection
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Note: Assigned participants will have "Involved in Exercise" set to Yes
          </p>
        </div>
      )}

      {/* Bulk Assign Confirmation Dialog */}
      {showBulkConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <Card className="bg-gray-800 border-gray-700 w-full max-w-md">
            <CardHeader>
              <CardTitle className={selectedExercise === 'no-exercise' ? 'text-red-500' : 'text-orange-500'}>
                {selectedExercise === 'no-exercise' ? 'Confirm Removal from Exercises' : 'Confirm Bulk Assignment'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedExercise === 'no-exercise' ? (
                <>
                  <p className="text-gray-300">
                    Are you sure you want to remove <strong className="text-red-400">{selectedParticipants.length} participant{selectedParticipants.length !== 1 ? 's' : ''}</strong> from their assigned exercises?
                  </p>
                  <p className="text-sm text-gray-400">
                    This will clear their exercise assignment and set "Involved in Exercise" to <strong>No</strong>.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-gray-300">
                    Are you sure you want to assign <strong className="text-orange-400">{selectedParticipants.length} participant{selectedParticipants.length !== 1 ? 's' : ''}</strong> to:
                  </p>
                  <p className="text-white font-semibold text-lg">
                    "{getSelectedExerciseName()}"
                  </p>
                  <p className="text-sm text-gray-400">
                    This will also set "Involved in Exercise" to <strong>Yes</strong> for all selected participants.
                  </p>
                </>
              )}
              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowBulkConfirm(false)}
                  className="border-gray-500 text-gray-300 hover:bg-gray-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkAssign}
                  disabled={bulkAssigning}
                  className={selectedExercise === 'no-exercise' 
                    ? 'bg-red-500 hover:bg-red-600 text-white font-semibold'
                    : 'bg-orange-500 hover:bg-orange-600 text-black font-semibold'
                  }
                >
                  {bulkAssigning 
                    ? (selectedExercise === 'no-exercise' ? 'Removing...' : 'Assigning...') 
                    : (selectedExercise === 'no-exercise' ? 'Confirm Removal' : 'Confirm Assignment')
                  }
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={`skeleton-${i}`} className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gray-700 rounded-full animate-pulse"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-700 rounded animate-pulse w-1/4"></div>
                    <div className="h-3 bg-gray-700 rounded animate-pulse w-1/2"></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredParticipants.length === 0 && searchTerm ? (
        <Card className="bg-gray-800 border-gray-700 border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Search className="h-12 w-12 text-gray-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2">No Results Found</h3>
            <p className="text-gray-500 text-center mb-4">
              No participants match "{searchTerm}"
            </p>
            <Button 
              onClick={() => setSearchTerm('')}
              variant="outline"
              className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
            >
              Clear Search
            </Button>
          </CardContent>
        </Card>
      ) : filteredParticipants.length === 0 && filter === 'participating' ? (
        <Card className="bg-gray-800 border-gray-700 border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-gray-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2">No Active Participants</h3>
            <p className="text-gray-500 text-center mb-4">
              No participants are currently marked as involved in the exercise.
            </p>
            <Button 
              onClick={() => setFilter('all')}
              variant="outline"
              className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
            >
              Show All Participants
            </Button>
          </CardContent>
        </Card>
      ) : filteredParticipants.length === 0 ? (
        <Card className="bg-gray-800 border-gray-700 border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-gray-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2">No Participants Yet</h3>
            <p className="text-gray-500 text-center mb-4">
              Add emergency team members to start building your exercise roster.
            </p>
            <Button 
              onClick={onAddNew}
              className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Participant
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredParticipants.map((participant) => (
            <Card 
              key={participant.id} 
              className={`bg-gray-800 border-gray-700 hover:border-orange-500/50 transition-colors ${
                selectedParticipants.includes(participant.id) ? 'border-orange-500 bg-orange-500/5' : ''
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-center space-x-4">
                  {/* Selection + Check-in Checkboxes */}
                  <div className="flex-shrink-0 flex flex-col space-y-2">
                    <button
                      type="button"
                      onClick={() => handleSelectParticipant(participant.id)}
                      className="flex items-center space-x-2 text-left hover:text-orange-400 transition-colors"
                      data-testid={`participant-selected-toggle-${participant.id}`}
                    >
                      {selectedParticipants.includes(participant.id) ? (
                        <CheckSquare className="h-5 w-5 text-orange-500" />
                      ) : (
                        <Square className="h-5 w-5 text-gray-500" />
                      )}
                      <span className={`text-xs font-medium ${selectedParticipants.includes(participant.id) ? 'text-orange-400' : 'text-gray-400'}`}>
                        Selected
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggleCheckedIn(participant)}
                      className="flex items-center space-x-2 text-left hover:text-green-400 transition-colors"
                      data-testid={`participant-checked-in-toggle-${participant.id}`}
                    >
                      {participant.checkedIn ? (
                        <CheckSquare className="h-5 w-5 text-green-500" />
                      ) : (
                        <Square className="h-5 w-5 text-gray-500" />
                      )}
                      <span className={`text-xs font-medium ${participant.checkedIn ? 'text-green-400' : 'text-gray-400'}`}>
                        Checked In
                      </span>
                    </button>
                  </div>

                  <Avatar className="w-16 h-16">
                    <AvatarImage src={participant.profileImage} alt={participant.name} />
                    <AvatarFallback className="bg-orange-500 text-black text-lg font-semibold">
                      {getInitials(participant.name)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold text-white truncate">
                        {(() => {
                          // Display "Last, First". Fall back to splitting the
                          // combined `name` field if firstName/lastName aren't set.
                          const parts = (participant.name || '').trim().split(/\s+/);
                          const last = participant.lastName || parts.slice(-1)[0] || '';
                          const first = participant.firstName || parts.slice(0, -1).join(' ') || '';
                          if (last && first) return `${last}, ${first}`;
                          return participant.name || last || first || 'Unnamed';
                        })()}
                      </h3>
                      {participant.involvedInExercise && (
                        <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                          Active Exercise
                        </Badge>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                      <div className="flex items-center space-x-2 text-gray-300">
                        <Target className="h-4 w-4 text-orange-500" />
                        <span>{participant.position || participant.role || 'No position'}</span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-gray-300">
                        <Target className="h-4 w-4 text-blue-500" />
                        <span>
                          {(() => {
                            const exerciseId = participant.assigned_exercise || participant.assignedExercise;
                            if (!exerciseId) return 'No exercise';
                            const exercise = exercises.find(e => e.id === exerciseId);
                            return exercise ? exercise.exercise_name : exerciseId;
                          })()}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-gray-300">
                        <Mail className="h-4 w-4 text-orange-500" />
                        <span className="truncate">{participant.email}</span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-gray-300">
                        <Phone className="h-4 w-4 text-orange-500" />
                        <span>{participant.cellPhone || participant.phone || 'No phone'}</span>
                      </div>
                    </div>
                    
                    {/* Additional row for address */}
                    <div className="mt-2 text-sm">
                      <div className="flex items-center space-x-2 text-gray-300">
                        <MapPin className="h-4 w-4 text-orange-500" />
                        <span className="truncate">
                          {participant.address || participant.city && participant.country ? 
                            `${participant.address || ''} ${participant.city}, ${participant.country}`.trim() : 
                            'No address'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(participant)}
                      className="border-gray-600 text-gray-300 hover:text-orange-500 hover:border-orange-500/50"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteParticipant(participant.id)}
                      className="border-red-600 text-red-400 hover:text-red-300 hover:border-red-500/50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};


export default ParticipantsList;
